"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { contactSchema, type ContactInput } from "@/features/contact/schema";
import { siteConfig } from "@/config/site";

const fieldClass =
  "w-full border-b border-white/70 bg-transparent px-0 py-3 text-base text-white placeholder:text-white/60 focus:border-b-2 focus:border-white focus:outline-none";

const labelClass =
  "block text-[11px] uppercase tracking-[0.18em] text-white/80";

const errorClass = "mt-2 text-sm text-white";

// Statický export nemá vlastní API. Odesílání jde na externí endpoint
// nakonfigurovaný při buildu (webhook / formulářová služba).
const CONTACT_ENDPOINT = process.env.NEXT_PUBLIC_CONTACT_ENDPOINT ?? "";

const FALLBACK_MESSAGE = `Nepodařilo se odeslat. Zkuste to znovu, nebo zavolejte na ${siteConfig.contact.phone} či napište na ${siteConfig.contact.email}.`;

export function ContactForm() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactInput>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
      consent: false,
      company: "",
    },
  });

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [serverMessage, setServerMessage] = useState("");

  const onSubmit = async (values: ContactInput) => {
    setServerMessage("");
    setStatus("idle");

    if (!CONTACT_ENDPOINT) {
      setServerMessage(
        `Odesílání formuláře není nakonfigurované. Zavolejte na ${siteConfig.contact.phone} nebo napište na ${siteConfig.contact.email}.`,
      );
      setStatus("error");
      return;
    }

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          phone: values.phone ?? "",
          message: values.message ?? "",
          consent: values.consent,
          source: "acomputer-firmy",
        }),
      });

      if (!response.ok) {
        setServerMessage(FALLBACK_MESSAGE);
        setStatus("error");
        return;
      }

      reset();
      setStatus("success");
    } catch {
      setServerMessage(FALLBACK_MESSAGE);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        className="border border-white p-6 md:p-8"
        role="status"
        aria-live="polite"
      >
        <p className="text-2xl font-bold text-white">Děkujeme, ozveme se.</p>
        <p className="mt-2 text-base text-white/85">Vaše poptávka dorazila.</p>
      </div>
    );
  }

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      className="border border-white p-6 md:p-8"
    >
      <div className="flex flex-col gap-6">
        <div>
          <label className={labelClass} htmlFor="contact-name">
            Jméno *
          </label>
          <input
            id="contact-name"
            type="text"
            autoComplete="name"
            className={fieldClass}
            placeholder="Jan Novák"
            aria-invalid={errors.name ? "true" : "false"}
            aria-describedby={errors.name ? "contact-name-error" : undefined}
            {...register("name")}
          />
          {errors.name ? (
            <p id="contact-name-error" role="alert" className={errorClass}>
              {errors.name.message}
            </p>
          ) : null}
        </div>

        <div>
          <label className={labelClass} htmlFor="contact-email">
            E-mail *
          </label>
          <input
            id="contact-email"
            type="email"
            autoComplete="email"
            className={fieldClass}
            placeholder="jan@firma.cz"
            aria-invalid={errors.email ? "true" : "false"}
            aria-describedby={errors.email ? "contact-email-error" : undefined}
            {...register("email")}
          />
          {errors.email ? (
            <p id="contact-email-error" role="alert" className={errorClass}>
              {errors.email.message}
            </p>
          ) : null}
        </div>

        <div>
          <label className={labelClass} htmlFor="contact-phone">
            Telefon nebo web
          </label>
          <input
            id="contact-phone"
            type="text"
            autoComplete="tel"
            className={fieldClass}
            placeholder="+420 …"
            {...register("phone")}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="contact-message">
            Zpráva
          </label>
          <textarea
            id="contact-message"
            rows={4}
            className={fieldClass}
            placeholder="Čemu se věnujete?"
            {...register("message")}
          />
        </div>

        <div
          className="absolute -left-[9999px] h-0 w-0 overflow-hidden"
          aria-hidden="true"
        >
          <label htmlFor="contact-company">Společnost</label>
          <input
            id="contact-company"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            {...register("company")}
          />
        </div>

        <div className="flex items-start gap-3">
          <input
            id="contact-consent"
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0 accent-white"
            aria-invalid={errors.consent ? "true" : "false"}
            aria-describedby={
              errors.consent ? "contact-consent-error" : undefined
            }
            {...register("consent")}
          />
          <label
            htmlFor="contact-consent"
            className="text-[12px] leading-relaxed text-white/85"
          >
            Souhlasím se zpracováním osobních údajů podle{" "}
            <a
              href={siteConfig.legal.privacyHref || "#"}
              className="underline underline-offset-2 hover:text-white"
            >
              {siteConfig.legal.privacy}
            </a>
            .
          </label>
        </div>
        {errors.consent ? (
          <p id="contact-consent-error" role="alert" className={errorClass}>
            {errors.consent.message}
          </p>
        ) : null}

        {status === "error" && serverMessage ? (
          <p
            role="alert"
            aria-live="polite"
            className="border border-white px-4 py-3 text-sm text-white"
          >
            {serverMessage}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={isSubmitting}
          className="group inline-flex min-h-[52px] items-center justify-center gap-3 bg-white px-8 text-[12px] font-bold uppercase tracking-[0.18em] text-ink-text transition-colors duration-200 hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <span>{isSubmitting ? "Odesílám…" : "Odeslat poptávku"}</span>
          <ArrowRight
            className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
            aria-hidden="true"
          />
        </button>
      </div>
    </form>
  );
}
